/**
 * CloseButton — overlay primitive that exits the camera surface and
 * returns the viewer to the map (or whatever the shell's "rest" mode is).
 *
 * Reads {@link useShellSurface}. When the host shell has a surface mode
 * concept AND we're currently in `camera`, the button shows and clicking
 * it requests `map`. Otherwise the button renders nothing — primitives
 * that don't belong on the page should disappear, not turn into broken
 * affordances.
 *
 * On shells without a surface concept (Le Vanilla), the button stays
 * hidden so authors can keep the same default layout across templates.
 */

import { X } from 'lucide-react'
import { registerPrimitive, useShellSurface } from '../../layout'

interface CloseButtonConfig {
  /** aria-label for the button. Defaults to "Close camera". */
  label?: string
}

export function CloseButton({ config }: { id?: string; config: Record<string, unknown> }) {
  const cfg = config as CloseButtonConfig
  const { mode, requestMode } = useShellSurface()

  // Only render when there's actually a non-default surface to leave.
  if (mode === null || mode !== 'camera') return null

  return (
    <button
      type="button"
      className="lv-player-ctrl"
      aria-label={cfg.label ?? 'Close camera'}
      title={cfg.label ?? 'Close'}
      onClick={(e) => {
        e.stopPropagation()
        requestMode('map')
      }}
    >
      <X size={16} strokeWidth={2} aria-hidden="true" />
    </button>
  )
}

registerPrimitive('CloseButton', CloseButton)
